/**
 * Reference: https://github.com/timc1/kbar/blob/main/src/useMatches.tsx
 * Implements substring matching for static commands and before doing so,
 * leaves the results from the search results that we get from the search index
 */

import { type ActionImpl, Priority, useKBar } from 'kbar';
import { useThrottledValue } from 'kbar/lib/utils';
import * as React from 'react';

import type { CommandBarActionImpl } from './actions/types';

export const NO_GROUP = {
  name: 'none',
  priority: Priority.NORMAL,
};

type SearchActionImpl = ActionImpl & { isSearchResult?: boolean };

function isSearchResultAction(action: ActionImpl): action is SearchActionImpl {
  return Boolean((action as SearchActionImpl).isSearchResult);
}

type ScoredActionImpl = ActionImpl & { match?: (query: string) => number };

function isScoredAction(
  action: ActionImpl
): action is ScoredActionImpl & { match: (query: string) => number } {
  return typeof (action as ScoredActionImpl).match === 'function';
}

const MAX_SCORED_MATCHES = 5;

function matchActions(actions: ActionImpl[], query: string): ActionImpl[] {
  const scored = actions
    .filter(isScoredAction)
    .map((action) => ({ action, score: action.match(query) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_SCORED_MATCHES)
    .map(({ action }) => action);

  const matches: ActionImpl[] = [];
  let scoredInserted = false;
  for (const action of actions) {
    if (isScoredAction(action)) {
      if (!scoredInserted) matches.push(...scored);
      scoredInserted = true;
    } else if (isSearchResultAction(action) || simpleTextMatch(action, query)) {
      matches.push(action);
    }
  }
  return matches;
}

const ROOT_SECTION_ORDER = ['Actions', 'Recent', 'Navigation', 'Settings'];

function getRootSectionRank(name: string, firstAction: ActionImpl | undefined) {
  if ((firstAction as CommandBarActionImpl | undefined)?.contextKind) {
    return 0;
  }
  const index = ROOT_SECTION_ORDER.indexOf(name);
  return index === -1 ? ROOT_SECTION_ORDER.length + 1 : index + 1;
}

function order(a: any, b: any) {
  /**
   * Larger the priority = higher up the list
   */
  return b.priority - a.priority;
}

export function simpleTextMatch(
  action: ActionImpl,
  searchQuery: string
): boolean {
  const query = searchQuery.toLowerCase();

  // Check name
  if (action.name.toLowerCase().includes(query)) {
    return true;
  }

  // Check subtitle
  if (
    action.subtitle &&
    typeof action.subtitle === 'string' &&
    action.subtitle.toLowerCase().includes(query)
  ) {
    return true;
  }

  // Check keywords
  if (action.keywords) {
    const keywords = action.keywords
      .split(',')
      .map((k) => k.trim().toLowerCase());
    return keywords.some((keyword) => keyword.includes(query));
  }

  return false;
}

type SectionName = string;

/**
 * Returns the list of command bar actions grouped and sorted for display. Includes search result actions at the bottom, and applies fuzzy matching to all others.
 */
export function useMatches() {
  const { search, actions, rootActionId } = useKBar((state) => ({
    search: state.searchQuery,
    actions: state.actions,
    rootActionId: state.currentRootActionId,
  }));

  const rootResults = React.useMemo(() => {
    // Separate search actions and others
    const allActions = Object.values(actions);
    const searchActions = allActions.filter(isSearchResultAction);
    const otherActions = allActions.filter((a) => !isSearchResultAction(a));

    // Use the existing logic for non-search actions
    const filteredOtherRootResults = otherActions
      .reduce((acc, action) => {
        if (!action.parent && !rootActionId) {
          acc.push(action);
        }
        if (action.id === rootActionId) {
          for (let i = 0; i < action.children.length; i++) {
            acc.push(action.children[i]);
          }
        }
        return acc;
      }, [] as ActionImpl[])
      .sort(order);

    if (rootActionId) {
      return filteredOtherRootResults;
    }

    // Always place search actions at the bottom
    return [...filteredOtherRootResults, ...searchActions];
  }, [actions, rootActionId]);

  // Flattens all children for deep searching when a query is present
  const getDeepResults = React.useCallback((actions: ActionImpl[]) => {
    const otherActions = actions.filter((a) => !isSearchResultAction(a));
    const searchActions = actions.filter(isSearchResultAction);

    function flatten(actions: ActionImpl[]): ActionImpl[] {
      return actions.flatMap((action) => [
        action,
        ...(action.children?.length ? flatten(action.children) : []),
      ]);
    }
    return [...flatten(otherActions), ...searchActions];
  }, []);

  const emptySearch = !search;

  // If no query, show only root actions; otherwise, flatten all for matching
  const filtered = React.useMemo(() => {
    if (emptySearch) return rootResults;
    return getDeepResults(rootResults);
  }, [getDeepResults, rootResults, emptySearch]);

  const matches = useInternalMatches(filtered, search);

  const results = React.useMemo(() => {
    /**
     * Store a reference to a section and it's list of actions.
     * Alongside these actions, we'll keep a temporary record of the
     * final priority calculated by taking the commandScore + the
     * explicitly set `action.priority` value.
     */
    const map: Record<SectionName, { priority: number; action: ActionImpl }[]> =
      {};
    /**
     * Store another reference to a list of sections alongside
     * the section's final priority, calculated the same as above.
     */
    const list: { priority: number; name: SectionName }[] = [];
    /**
     * We'll take the list above and sort by its priority. Then we'll
     * collect all actions from the map above for this specific name and
     * sort by its priority as well.
     */
    let ordered: { name: SectionName; actions: ActionImpl[] }[] = [];

    for (let i = 0; i < matches.length; i++) {
      const action = matches[i];
      const score = Priority.NORMAL;

      const section = {
        name:
          typeof action.section === 'string'
            ? action.section
            : action.section?.name || NO_GROUP.name,
        priority:
          typeof action.section === 'string'
            ? score
            : action.section?.priority || 0 + score,
      };

      if (!map[section.name]) {
        map[section.name] = [];
        list.push(section);
      }

      map[section.name].push({
        priority: action.priority + score,
        action,
      });
    }

    ordered = list.sort(order).map((group) => ({
      name: group.name,
      actions: map[group.name].sort(order).map((item) => item.action),
    }));

    if (emptySearch && !rootActionId) {
      ordered = ordered.sort(
        (a, b) =>
          getRootSectionRank(a.name, a.actions[0]) -
          getRootSectionRank(b.name, b.actions[0])
      );
    }

    /**
     * Our final result is simply flattening the ordered list into
     * our familiar (ActionImpl | string)[] shape.
     */
    const results: (string | ActionImpl)[] = [];
    for (let i = 0; i < ordered.length; i++) {
      const group = ordered[i];
      if (group.name !== NO_GROUP.name) results.push(group.name);
      for (let i = 0; i < group.actions.length; i++) {
        results.push(group.actions[i]);
      }
    }
    return results;
  }, [matches, emptySearch, rootActionId]);

  // ensure that users have an accurate `currentRootActionId`
  // that syncs with the throttled return value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoRootActionId = React.useMemo(() => rootActionId, [results]);

  return React.useMemo(
    () => ({
      results,
      rootActionId: memoRootActionId,
    }),
    [memoRootActionId, results]
  );
}

// Applies simple text matching to filter actions based on the search query
function useInternalMatches(filtered: ActionImpl[], search: string) {
  const value = React.useMemo(
    () => ({
      filtered,
      search,
    }),
    [filtered, search]
  );

  const { filtered: throttledFiltered, search: throttledSearch } =
    useThrottledValue(value);

  return React.useMemo(() => {
    if (throttledSearch.trim() === '') {
      return throttledFiltered;
    }
    return matchActions(throttledFiltered, throttledSearch);
  }, [throttledFiltered, throttledSearch]);
}
